/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogActions, TextField, Box, InputAdornment } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { TimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';

import {
  ComposeQuarterEntry,
  convertToQuarterlyFormat,
  formatDate,
  FormatDisplayToPayload,
} from '../../../../../Shared/Utilities/utils';
import { FieldNames } from '../../../../Jobs/Models/AddEditOneTimeJob.Model';
import {
  DayOptions,
  InitialQuarters,
  KeyBoardEvent,
  ScheduleTypes,
  SelectedDuration,
  ShiftTypes,
  WeekOptions,
} from '../../../../../Shared/Constants/App';
import { BoxShadow } from '../../../../../Shared/Constants/Style.Constants';
import {
  IFormValues,
  IModifySchedulePopupProps,
  numericToDayMap,
  PatternType,
  PatternTypes,
  quarterOptions,
  WEEK_DAYS,
} from '../AllSchedules.Model';
import DeletePopup from '../../../../../Shared/Components/Common/DeletePopup/DeletePopup';
import Button, { ButtonType } from '../../../../../Shared/Components/Common/Buttons/Button';
import AnnualSchedule from './Frequencies/AnnualSchedule';
import BiAnnualSchedule from './Frequencies/BiAnnualSchedule';
import QuarterlySchedule from './Frequencies/QuarterlySchedule';
import MonthlySchedule from './Frequencies/MonthlySchedule';
import WeeklySchedule from './Frequencies/WeeklySchedule';

import { Icons } from '../../../../../Shared/Constants/Icons';
import theme from '../../../../../Shared/Themes/theme';

import {
  buttonStyles,
  DateFieldWrapper,
  ErrorMsg,
} from '../../../../Jobs/Components/AddEditOneTimeJob/AddEditOneTimeJob.Style';
import {
  EditableOptionContainer,
  ModifyContainer,
  ModifyScheduleText,
  ScheduleDaysText,
  wrapperStyles,
} from '../AllJobs.Style';
import { DeleteWrapper } from '../../../../../Shared/Components/Common/DeletePopup/DeletePopup.Style';
import { isEmpty } from 'lodash';
import { Labels } from '../../../../../Shared/Components/Common/GraphContainer/GraphContainer';
import DateSelection from '../../../../../Shared/Components/Common/DatePicker/DateSelection';
import { CreateScheduleFieldNames } from '../../AllJobs/AllJobs.Model';

const ModifySchedulePopup: React.FC<IModifySchedulePopupProps> = ({
  open,
  onClose,
  onConfirm,
  selectedJobData,
  isDeletePopupVisible,
  setIsDeletePopupVisible,
  isLoading,
  setIsLoading,
}) => {
  const [selectedWeek, setSelectedWeek] = useState('');
  const [selectedDayOfWeek, setSelectedDayOfWeek] = useState('');
  const [isWeekOpen, setIsWeekOpen] = useState(false);
  const [isDayOfWeekOpen, setDayOfWeekOpen] = useState(false);
  const [applyToAllQuarters, setApplyToAllQuarters] = useState(false);
  const [selectedDays, setSelectedDays] = useState<string[]>([]);
  const [selectedShiftType, setSelectedShiftType] = useState(selectedJobData?.[0]?.mdShiftType);
  const [scheduleName, setScheduleName] = useState(selectedJobData[0]?.scheduleName || '');
  const [isEditingScheduleName, setIsEditingScheduleName] = useState(false);
  const [endDateOffset, setEndDateOffset] = useState(selectedJobData[0]?.endDateOffset || '');
  const [isEditingEndDateOffset, setIsEditingEndDateOffset] = useState(false);
  const [isMonthDropdownOpen, setIsMonthDropdownOpen] = useState(false);
  const [isWeekDropdownOpen, setIsWeekDropdownOpen] = useState(false);
  const [isDayDropdownOpen, setIsDayDropdownOpen] = useState(false);
  const [dropdownOpenStates, setDropdownOpenStates] = useState<Record<string, boolean>>({});
  const [isEditingTotalFTE, setIsEditingTotalFTE] = useState(false);
  const [totalFTE, setTotalFTE] = useState(selectedJobData[0]?.totalFTE || '');
  const totalFTEInputRef = useRef<HTMLInputElement>(null);

  const getStateKey = (quarter: string, type: string) => `${quarter}-${type}`;

  const toggleWeekValue = () => {
    setIsWeekOpen(!isWeekOpen);
  };
  const toggleDayOfWeekValue = () => {
    setDayOfWeekOpen(!isDayOfWeekOpen);
  };

  const { control, handleSubmit, reset, setValue, getValues, watch } = useForm<IFormValues>({
    defaultValues: {
      startTime: (selectedJobData[0]?.scheduleStartTime || '').slice(0, 5),
      endTime: (selectedJobData[0]?.scheduleEndTime || '').slice(0, 5),
      woScheduleMasterId: selectedJobData[0]?.woScheduleMasterId,
      scheduleDays: selectedJobData[0]?.scheduleDays,
      shiftType: selectedJobData?.[0]?.mdShiftType,
      scheduleName: selectedJobData[0]?.scheduleName || '',
      endDateOffset: selectedJobData[0]?.endDateOffset || '',
      selectedQuarter: SelectedDuration.FirstQuarter,
      selectedMonth: SelectedDuration.FirstMonth,
      selectedWeek: SelectedDuration.FirstWeek,
      selectedDay: SelectedDuration.FirstDay,
      quarters: {
        Q1: { month: SelectedDuration.FirstMonth, week: SelectedDuration.FirstWeek, day: SelectedDuration.FirstDay },
        Q2: { month: SelectedDuration.FirstMonth, week: SelectedDuration.FirstWeek, day: SelectedDuration.FirstDay },
        Q3: { month: SelectedDuration.FirstMonth, week: SelectedDuration.FirstWeek, day: SelectedDuration.FirstDay },
        Q4: { month: SelectedDuration.FirstMonth, week: SelectedDuration.FirstWeek, day: SelectedDuration.FirstDay },
      },
      patternType: PatternTypes.Monthly,
      totalFTE: selectedJobData[0]?.totalFTE || '',
      scheduleStartDate: new Date(),
      scheduleEndDate: null,
    },
  });

  const patternType = watch('patternType');
  const endDateOffsetInputRef = useRef<HTMLInputElement>(null);
  const scheduleNameInputRef = useRef<HTMLInputElement>(null);
  const { closeIconStyle } = buttonStyles;
  const { daysStyle, frequencyOptions, textField, radioButton, radioButtonText, textStyle, quarterBoxStyle } =
    wrapperStyles;
  const { t } = useTranslation(['jobManagement', 'messages', 'homePage']);
  const startTime = watch('startTime');
  const endTime = watch('endTime');
  const disableConfirmButton = !startTime || !endTime;
  const previousQuartersDataRef = useRef<Record<string, any>>({});

  const handleEndDateOffsetEdit = () => {
    setIsEditingEndDateOffset(true);
    endDateOffsetInputRef.current?.focus();
  };

  const handleEndDateOffsetDone = () => {
    setIsEditingEndDateOffset(false);
  };

  const handleEndDateOffsetKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === KeyBoardEvent.Enter.toString()) {
      handleEndDateOffsetDone();
    }
  };

  const handleTotalFTEEdit = () => {
    setIsEditingTotalFTE(true);
    setTimeout(() => totalFTEInputRef.current?.focus(), 100);
  };

  const handleModifyShiftType = (shiftType: string) => {
    setSelectedShiftType(shiftType);
  };

  const handleScheduleNameEdit = () => {
    setIsEditingScheduleName(true);
    scheduleNameInputRef.current?.focus();
  };

  const handleScheduleNameDone = () => {
    setIsEditingScheduleName(false);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === KeyBoardEvent.Enter.toString()) {
      handleScheduleNameDone();
    }
  };

  const handleDayClick = (day: string) => {
    setSelectedDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
  };

  const updateAllQuarters = (fieldType: string, value: string) => {
    quarterOptions.forEach((q) => {
      return setValue(`quarters.${q}.${fieldType}` as any, value);
    });
  };

  const formatAnnual = (scheduleDays: string | string[]): string => {
    const raw = Array.isArray(scheduleDays) ? scheduleDays[0] : scheduleDays;

    if (typeof raw === 'string') {
      if (raw.startsWith('M')) return raw;
      if (raw.includes('-')) {
        const [month, day] = raw.split('-');
        return `M${parseInt(month)}-${parseInt(day)}`;
      }
      return raw;
    }

    return '';
  };

  const formatMonthly = (): string => {
    if (selectedDays.length > 0 && !selectedWeek) {
      return selectedDays.map((day) => `M-${day}`).join(',');
    }

    if (selectedWeek && selectedDayOfWeek) {
      const dayIndex = DayOptions.findIndex((d) => d.value === selectedDayOfWeek);
      return selectedWeek === 'last' ? `L-${dayIndex}` : `W${selectedWeek.replace('week', '')}-${dayIndex}`;
    }

    return '';
  };

  const formatQuarterly = (data: IFormValues): string => {
    if (data.quarters && data.patternType) {
      return applyToAllQuarters
        ? [`Q-${ComposeQuarterEntry(data.patternType, data.quarters.Q1)}`].join(',')
        : Object.entries(data.quarters)
            .map(([quarter, config]) => `${quarter}-${ComposeQuarterEntry(data.patternType, config)}`)
            .join(',');
    }

    if (Array.isArray(data.scheduleDays)) {
      return data.scheduleDays.map((day) => (day.startsWith('Q') ? day : convertToQuarterlyFormat(day))).join(',');
    }

    return data.scheduleDays.startsWith('Q') ? data.scheduleDays : convertToQuarterlyFormat(data.scheduleDays);
  };

  const getFormattedScheduleDays = (scheduleType: string, data: IFormValues): string => {
    switch (scheduleType) {
      case ScheduleTypes.Annual:
        return formatAnnual(data.scheduleDays);

      case ScheduleTypes.Fortnightly:
      case ScheduleTypes.Weekly:
        return selectedDays
          .map((day) => numericToDayMap.indexOf(day))
          .filter((day) => day >= 0)
          .join(',');

      case ScheduleTypes.Monthly:
        return formatMonthly();

      case ScheduleTypes.BiAnnual:
        return Array.isArray(data.scheduleDays)
          ? data.scheduleDays.map((day) => (day.startsWith('M') ? day : FormatDisplayToPayload(day))).join(',')
          : data.scheduleDays;

      case ScheduleTypes.Quarterly:
        return formatQuarterly(data);

      default:
        return '';
    }
  };

  const handleFormSubmit = async (data: IFormValues) => {
    setIsLoading(true);
    try {
      const scheduleType = selectedJobData[0]?.mdScheduleType;
      const formattedScheduleDays = getFormattedScheduleDays(scheduleType, data);
      await new Promise<void>(() => {
        onConfirm({
          woScheduleMasterId: data?.woScheduleMasterId,
          scheduleDays: formattedScheduleDays,
          startTime: data?.startTime,
          endTime: data?.endTime,
          shiftType: selectedShiftType,
          scheduleName: data?.scheduleName,
          endDateOffset: data?.endDateOffset,
          totalFTE: Number(data?.totalFTE ?? 0),
          scheduleStartDate: new Date(),
          scheduleEndDate: null,
        });
      });
    } catch (error) {
      console.error(t('messages:errorOccurred'), error);
    } finally {
      setIsLoading(false);
    }
    reset();
    onClose();
  };

  useEffect(() => {
    if (selectedJobData && selectedJobData.length > 0) {
      const {
        scheduleStartTime,
        scheduleEndTime,
        woScheduleMasterId,
        scheduleDays,
        shiftType,
        scheduleName,
        endDateOffset,
        totalFTE,
      } = selectedJobData[0];
      const startTime = scheduleStartTime?.slice(0, 5);
      const endTime = scheduleEndTime?.slice(0, 5);
      setValue('startTime', startTime || '');
      setValue('endTime', endTime || '');
      setValue('woScheduleMasterId', woScheduleMasterId || '');
      setValue('scheduleDays', scheduleDays || '');
      setValue('shiftType', shiftType || '');
      setValue('scheduleName', scheduleName || '');
      setScheduleName(scheduleName || '');
      setValue('endDateOffset', endDateOffset || '');
      setEndDateOffset(endDateOffset || '');
      setValue('totalFTE', totalFTE || '');
      setTotalFTE(totalFTE || '');
    }
  }, [selectedJobData, setValue]);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    if (
      (selectedJobData[0]?.mdScheduleType === ScheduleTypes.Weekly && selectedJobData[0]?.scheduleDays) ||
      (selectedJobData[0]?.mdScheduleType === ScheduleTypes.Fortnightly && selectedJobData[0]?.scheduleDays)
    ) {
      const daysFromData = selectedJobData[0]?.scheduleDays.map((day: string) => {
        return numericToDayMap[parseInt(day)];
      });
      setSelectedDays(daysFromData);
    } else if (selectedJobData[0]?.mdScheduleType === ScheduleTypes.Monthly) {
      // Handle both  format ("0,1,2" and "M-20")
      const scheduleDays = selectedJobData[0]?.scheduleDays;
      if (Array.isArray(scheduleDays)) {
        // New format: ["M-20"]
        const days = scheduleDays
          .map((day) => {
            const parts = day.split('-');
            return parts.length === 2 ? parts[1] : null;
          })
          .filter((day) => day !== null);
        setSelectedDays(days);
      } else if (typeof scheduleDays === 'string') {
        // Old format: "0,1,2"
        setSelectedDays(scheduleDays.split(','));
      }
      // Continue with week/day dropdown logic
      const scheduleDay = selectedJobData[0]?.scheduleDays?.[0];
      if (scheduleDay) {
        const [weekPart, dayPart] = scheduleDay.split('-');
        // Set week dropdown
        if (weekPart.startsWith('W')) {
          const weekNum = weekPart.substring(1);
          setSelectedWeek(`week${weekNum}`);
        } else if (weekPart === 'L') {
          setSelectedWeek('last');
        }
        setSelectedDayOfWeek(WEEK_DAYS[parseInt(dayPart)] || '');
      }
    }
    if (!isEmpty(selectedJobData[0]?.mdShiftType)) {
      setSelectedShiftType(selectedJobData[0]?.mdShiftType);
    }
    if (!isEmpty(selectedJobData[0]?.mdShiftType)) {
      setSelectedShiftType(selectedJobData[0]?.mdShiftType);
    }
  }, [selectedJobData]);

  useEffect(() => {
    if (selectedWeek && selectedDayOfWeek) {
      let weekCode = '';
      if (selectedWeek === 'last') {
        weekCode = 'L';
      } else {
        weekCode = `W${selectedWeek.replace('week', '')}`;
      }
      const dayNumber = WEEK_DAYS.map((d) => d.toLowerCase()).indexOf(selectedDayOfWeek.toLowerCase()) + 1;
      const newScheduleDay = `${weekCode}-${dayNumber}`;
      setSelectedDays([newScheduleDay]);
      setValue('scheduleDays', [newScheduleDay]);
    }
  }, [selectedWeek, selectedDayOfWeek, setValue]);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    if (selectedJobData[0]?.scheduleDays) {
      let detectedPattern: PatternType = PatternTypes.Monthly;
      const firstEntry = selectedJobData[0].scheduleDays[0];

      if (firstEntry) {
        const parts = firstEntry.split('-');
        if (
          parts.length >= 4 &&
          parts.some((p: string) => p.startsWith('M')) &&
          parts.some((p: string) => p.startsWith('W'))
        ) {
          detectedPattern = PatternTypes.Monthly;
        } else if (parts.some((p: string) => p.startsWith('W')) && !parts.some((p: string) => p.startsWith('M'))) {
          detectedPattern = PatternTypes.Weekly;
        } else if (parts.some((p: string) => p.startsWith('M')) && !parts.some((p: string) => p.startsWith('W'))) {
          detectedPattern = PatternTypes.SpecificDay;
        }
      }
      // Process all entries
      selectedJobData[0].scheduleDays.forEach((entry: string) => {
        const [quarterPrefix, ...parts] = entry.split('-');
        // Handle "Q" entries for all quarters
        const targetQuarters = quarterPrefix === 'Q' ? quarterOptions : [quarterPrefix];
        targetQuarters.forEach((quarter) => {
          if (quarter in InitialQuarters) {
            const config: Record<string, string> = {};

            switch (detectedPattern) {
              case PatternTypes.Monthly:
                config.month = parts[0] || SelectedDuration.FirstMonth;
                config.week = parts[1] || SelectedDuration.FirstWeek;
                config.day = parts[2] || SelectedDuration.FirstDay;
                break;
              case PatternTypes.Weekly:
                config.week = parts[0] || SelectedDuration.FirstWeek;
                config.day = parts[1] || SelectedDuration.FirstDay;
                break;
              case PatternTypes.SpecificDay:
                config.month = parts[0] || SelectedDuration.FirstMonth;
                config.day = parts[1] || SelectedDuration.FirstDay;
                break;
            }
            InitialQuarters[quarter as keyof typeof InitialQuarters] = {
              ...InitialQuarters[quarter as keyof typeof InitialQuarters],
              ...config,
            };
          }
        });
      });
      setValue('quarters', InitialQuarters);
      setValue('patternType', detectedPattern);
    }
  }, [selectedJobData, setValue]);

  return (
    <DeleteWrapper open={open} onClose={onClose} manualWidth="45.25rem">
      <Box sx={closeIconStyle} onClick={onClose}>
        <img src={Icons.InfoWindowCloseIcon} />
      </Box>
      <ModifyScheduleText>{t('jobManagement:modifyJobSchedule')}</ModifyScheduleText>
      <ModifyContainer>
        <Box>
          <form onSubmit={handleSubmit(handleFormSubmit)}>
            {/* Schedule Name */}
            <Box sx={{ display: 'flex', alignItems: 'center', padding: '0.75rem 0' }}>
              {isEditingScheduleName ? (
                <Controller
                  control={control}
                  name="scheduleName"
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      fullWidth
                      sx={textField}
                      value={value}
                      onChange={(e) => {
                        onChange(e);
                        setScheduleName(e.target.value);
                      }}
                      onKeyDown={handleKeyDown}
                      inputRef={scheduleNameInputRef}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {value && (
                              <img
                                src={Icons.CancelIcon}
                                onClick={() => {
                                  onChange('');
                                  setScheduleName('');
                                }}
                                style={{ cursor: 'pointer' }}
                              />
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ScheduleDaysText sx={{ marginBottom: 0, paddingRight: '0.5rem' }}>
                    {scheduleName || '-'}
                  </ScheduleDaysText>
                  <img
                    src={Icons.EditBlack}
                    style={{ width: '1.5rem', height: '1.5rem', cursor: 'pointer' }}
                    onClick={handleScheduleNameEdit}
                  />
                </Box>
              )}
            </Box>
            {/* Type Selection */}
            <ScheduleDaysText>{t('jobManagement:frequency')}</ScheduleDaysText>
            <EditableOptionContainer>
              {[
                ScheduleTypes.Annual,
                ScheduleTypes.BiAnnual,
                ScheduleTypes.Quarterly,
                ScheduleTypes.Monthly,
                ScheduleTypes.Fortnightly,
                ScheduleTypes.Weekly,
                ScheduleTypes.Daily,
              ].map((type) => {
                const isDisabled = selectedJobData[0]?.mdScheduleType !== type;
                return (
                  <Box
                    key={type}
                    onClick={() => !isDisabled}
                    bgcolor={selectedJobData[0]?.mdScheduleType === type ? theme.palette.secondary.dark : ''}
                    fontWeight={
                      selectedJobData[0]?.mdScheduleType === type
                        ? theme.typography.fontWeightBold
                        : theme.typography.fontWeightLight
                    }
                    sx={{
                      ...frequencyOptions,
                      whiteSpace: 'nowrap',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      opacity: isDisabled ? 0.5 : 1,
                    }}>
                    {type}
                  </Box>
                );
              })}
            </EditableOptionContainer>
            {/* Conditional Sections */}
            {selectedJobData[0]?.mdScheduleType === ScheduleTypes.Annual && (
              <AnnualSchedule
                control={control}
                textFieldStyle={textField}
                label={<ScheduleDaysText>{t('jobManagement:serviceDays')}</ScheduleDaysText>}
              />
            )}

            {selectedJobData[0]?.mdScheduleType === ScheduleTypes.BiAnnual && (
              <BiAnnualSchedule
                control={control}
                textFieldStyle={textField}
                label={<ScheduleDaysText>{t('jobManagement:serviceDays')}</ScheduleDaysText>}
              />
            )}

            {selectedJobData[0]?.mdScheduleType === ScheduleTypes.Quarterly && (
              <QuarterlySchedule
                t={t}
                patternType={patternType}
                setValue={setValue}
                getValues={getValues}
                applyToAllQuarters={applyToAllQuarters}
                setApplyToAllQuarters={setApplyToAllQuarters}
                previousQuartersDataRef={previousQuartersDataRef}
                isLoading={isLoading}
                control={control}
                updateAllQuarters={updateAllQuarters}
                isMonthDropdownOpen={isMonthDropdownOpen}
                setIsMonthDropdownOpen={setIsMonthDropdownOpen}
                isWeekDropdownOpen={isWeekDropdownOpen}
                setIsWeekDropdownOpen={setIsWeekDropdownOpen}
                isDayDropdownOpen={isDayDropdownOpen}
                setIsDayDropdownOpen={setIsDayDropdownOpen}
                dropdownOpenStates={dropdownOpenStates}
                setDropdownOpenStates={setDropdownOpenStates}
                getStateKey={getStateKey}
                radioButton={radioButton}
                radioButtonText={radioButtonText}
                textStyle={textStyle}
                quarterBoxStyle={quarterBoxStyle}
              />
            )}

            {selectedJobData[0]?.mdScheduleType === ScheduleTypes.Monthly && (
              <MonthlySchedule
                t={t}
                selectedJobData={selectedJobData}
                selectedDays={selectedDays}
                handleDayClick={handleDayClick}
                selectedWeek={selectedWeek}
                setSelectedWeek={setSelectedWeek}
                isWeekOpen={isWeekOpen}
                toggleWeekValue={toggleWeekValue}
                WeekOptions={WeekOptions}
                selectedDayOfWeek={selectedDayOfWeek}
                setSelectedDayOfWeek={setSelectedDayOfWeek}
                isDayOfWeekOpen={isDayOfWeekOpen}
                toggleDayOfWeekValue={toggleDayOfWeekValue}
                DayOptions={DayOptions}
                daysStyle={daysStyle}
                Labels={Labels}
              />
            )}

            {selectedJobData[0]?.mdScheduleType === ScheduleTypes.Fortnightly && (
              <WeeklySchedule
                t={t}
                selectedDays={selectedDays}
                handleDayClick={handleDayClick}
                daysStyle={daysStyle}
                numericToDayMap={numericToDayMap}
                showLabel={true}
              />
            )}

            {selectedJobData[0]?.mdScheduleType === ScheduleTypes.Weekly && (
              <WeeklySchedule
                t={t}
                selectedDays={selectedDays}
                handleDayClick={handleDayClick}
                daysStyle={daysStyle}
                numericToDayMap={numericToDayMap}
                showLabel={true}
              />
            )}
            {/* Shift Type Selection */}
            <ScheduleDaysText sx={{ marginTop: '1rem' }}>{t('jobManagement:shiftType')}</ScheduleDaysText>
            <EditableOptionContainer>
              {[ShiftTypes.Day, ShiftTypes.Night, ShiftTypes.DayAndNight].map((shiftType) => {
                const isSelected = selectedShiftType === shiftType;
                return (
                  <Box
                    key={shiftType}
                    onClick={() => handleModifyShiftType(shiftType)}
                    bgcolor={isSelected ? theme.palette.secondary.dark : ''}
                    fontWeight={isSelected ? theme.typography.fontWeightBold : theme.typography.fontWeightLight}
                    sx={{
                      ...frequencyOptions,
                      cursor: 'pointer',
                    }}>
                    {shiftType}
                  </Box>
                );
              })}
            </EditableOptionContainer>
            {/* Time Selection */}
            <ScheduleDaysText>{t('jobManagement:shiftTime')}</ScheduleDaysText>
            <EditableOptionContainer>
              <Controller
                control={control}
                name={FieldNames.startTime}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <DateFieldWrapper>
                    <TimePicker
                      label={
                        <span>
                          {t('jobManagement:startTime')}
                          <span className="mandatory-field">*</span>
                        </span>
                      }
                      value={value ? dayjs(value, 'HH:mm') : null}
                      onChange={(newValue) => {
                        const formatted = newValue ? newValue.format('HH:mm') : '';
                        onChange(formatted);
                      }}
                      slotProps={{
                        textField: {
                          sx: textField,
                          error: !!error,
                          helperText: error?.message,
                        },
                        popper: {
                          sx: {
                            '.MuiPaper-root': {
                              boxShadow: BoxShadow.BoxShadow,
                              '.MuiMultiSectionDigitalClockSection-item.Mui-selected': {
                                backgroundColor: theme.palette.primary.main,
                                fontWeight: theme.typography.fontWeightBold,
                              },
                              '.MuiMultiSectionDigitalClockSection-item:hover': {
                                backgroundColor: theme.palette.secondary.dark,
                              },
                              '.MuiButton-root': {
                                color: theme.palette.common.white,
                                backgroundColor: theme.palette.primary.dark,
                              },
                            },
                          },
                        },
                      }}
                    />
                    <ErrorMsg>{error && error?.message}</ErrorMsg>
                  </DateFieldWrapper>
                )}
              />
              <Controller
                control={control}
                name={FieldNames.endTime}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <DateFieldWrapper>
                    <TimePicker
                      label={
                        <span>
                          {t('jobManagement:endTime')}
                          <span className="mandatory-field">*</span>
                        </span>
                      }
                      value={value ? dayjs(value, 'HH:mm') : null}
                      onChange={(newValue) => {
                        const formatted = newValue ? newValue.format('HH:mm') : '';
                        onChange(formatted);
                      }}
                      slotProps={{
                        textField: {
                          sx: textField,
                          error: !!error,
                          helperText: error?.message,
                        },
                        popper: {
                          sx: {
                            '.MuiPaper-root': {
                              boxShadow: BoxShadow.BoxShadow,
                              '.MuiMultiSectionDigitalClockSection-item.Mui-selected': {
                                backgroundColor: theme.palette.primary.main,
                                fontWeight: theme.typography.fontWeightBold,
                              },
                              '.MuiMultiSectionDigitalClockSection-item:hover': {
                                backgroundColor: theme.palette.secondary.dark,
                              },
                              '.MuiButton-root': {
                                color: theme.palette.common.white,
                                backgroundColor: theme.palette.primary.dark,
                              },
                            },
                          },
                        },
                      }}
                    />
                    <ErrorMsg>{error && error?.message}</ErrorMsg>
                  </DateFieldWrapper>
                )}
              />
            </EditableOptionContainer>
            <ScheduleDaysText>{t('jobManagement:scheduleDates')}</ScheduleDaysText>
            <EditableOptionContainer>
              <Box sx={{ width: '100%' }}>
                <DateSelection
                  requiredText={t('calendar:dateFieldIsRequired')}
                  control={control}
                  fieldName={CreateScheduleFieldNames.scheduleStartDate}
                  label={t('jobManagement:scheduleStartDate')}
                  isExcludePastDate={true}
                  isExcludeFutureDate={false}
                  showSpecificRangeDates={{
                    from: new Date(),
                    to: selectedJobData?.[0]?.jobEndDate && formatDate(selectedJobData?.[0]?.jobEndDate),
                  }}
                />
              </Box>
              <Box sx={{ width: '100%' }}>
                <DateSelection
                  requiredText={t('calendar:dateFieldIsRequired')}
                  control={control}
                  fieldName={CreateScheduleFieldNames.scheduleEndDate}
                  label={t('jobManagement:scheduleEndDate')}
                  isExcludePastDate={true}
                  isExcludeFutureDate={false}
                  isOptional={true}
                  showSpecificRangeDates={{
                    from: new Date(),
                    to: selectedJobData?.[0]?.jobEndDate && formatDate(selectedJobData?.[0]?.jobEndDate),
                  }}
                />
              </Box>
            </EditableOptionContainer>
            {selectedJobData[0]?.mdScheduleType !== ScheduleTypes.Daily && (
              <Box sx={{ display: 'flex', alignItems: 'center', padding: '0.75rem 0' }}>
                <ScheduleDaysText sx={{ marginBottom: 0, marginRight: '1rem', whiteSpace: 'nowrap' }}>
                  {t('jobManagement:endDateOffset')}:
                </ScheduleDaysText>
                {isEditingEndDateOffset ? (
                  <Controller
                    control={control}
                    name="endDateOffset"
                    render={({ field: { onChange, value } }) => (
                      <TextField
                        fullWidth
                        sx={textField}
                        value={value}
                        onChange={(e) => {
                          onChange(e);
                          setEndDateOffset(e.target.value);
                        }}
                        onKeyDown={handleEndDateOffsetKeyDown}
                        inputRef={endDateOffsetInputRef}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              {value && (
                                <img
                                  src={Icons.CancelIcon}
                                  onClick={() => {
                                    onChange('');
                                    setEndDateOffset('');
                                  }}
                                  style={{ cursor: 'pointer' }}
                                />
                              )}
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ScheduleDaysText sx={{ marginBottom: 0, paddingRight: '0.5rem' }}>
                      {endDateOffset || 0}
                    </ScheduleDaysText>
                    <img
                      src={Icons.EditBlack}
                      style={{ width: '1.5rem', height: '1.5rem', cursor: 'pointer' }}
                      onClick={handleEndDateOffsetEdit}
                    />
                  </Box>
                )}
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', padding: '0.75rem 0' }}>
              <ScheduleDaysText sx={{ marginBottom: 0, marginRight: '1rem', whiteSpace: 'nowrap' }}>
                {t('jobManagement:totalFullTimeEmployees')}:
              </ScheduleDaysText>
              {isEditingTotalFTE ? (
                <Controller
                  control={control}
                  name="totalFTE"
                  render={({ field: { onChange, value } }) => (
                    <TextField
                      fullWidth
                      type="number"
                      sx={{
                        ...textField,
                        '& input[type=number]': {
                          MozAppearance: 'textfield',
                        },
                        '& input[type=number]::-webkit-outer-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                        '& input[type=number]::-webkit-inner-spin-button': {
                          WebkitAppearance: 'none',
                          margin: 0,
                        },
                      }}
                      value={value}
                      onChange={(e) => {
                        const newValue = e?.target?.value;
                        if (newValue === '' || Number(newValue) > 0) {
                          onChange(e);
                          setTotalFTE(newValue);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (['-', 'e', '+'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      inputRef={totalFTEInputRef}
                      InputProps={{
                        inputProps: { min: 1 },
                        endAdornment: (
                          <InputAdornment position="end">
                            {value && (
                              <img
                                src={Icons.CancelIcon}
                                onClick={() => {
                                  onChange('1');
                                  setTotalFTE('1');
                                }}
                                style={{ cursor: 'pointer' }}
                              />
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ScheduleDaysText sx={{ marginBottom: 0, paddingRight: '0.5rem' }}>{totalFTE || 0}</ScheduleDaysText>
                  <img
                    src={Icons.EditBlack}
                    style={{ width: '1.5rem', height: '1.5rem', cursor: 'pointer' }}
                    onClick={handleTotalFTEEdit}
                  />
                </Box>
              )}
            </Box>
          </form>
        </Box>
      </ModifyContainer>
      <DialogActions sx={{ paddingTop: '0.5rem' }}>
        <Button type={ButtonType.Secondary} label={t('dashboard:cancel')} onClick={onClose} />
        <Button
          type={ButtonType.Primary}
          label={t('dashboard:confirm')}
          onClick={() => {
            setIsDeletePopupVisible(true);
          }}
          disabled={disableConfirmButton}
        />
      </DialogActions>
      <DeletePopup
        open={isDeletePopupVisible}
        onClose={() => setIsDeletePopupVisible(false)}
        message={t('jobManagement:confirmTheChanges')}
        primaryButtonLabel={t('homePage:yes')}
        name={''}
        isPrimaryButtonLoading={isLoading}
        primaryAction={handleSubmit(handleFormSubmit)}
        secondaryButtonLabel={t('homePage:no')}
        disabled={isLoading}
        secondaryAction={() => {
          setIsDeletePopupVisible(false);
        }}></DeletePopup>
    </DeleteWrapper>
  );
};

export default ModifySchedulePopup;
